import type {PerkADto} from '@cohort/admin-schemas/perk';
import AdminPage from '@cohort/merchants/components/AdminPage';
import Button from '@cohort/merchants/components/buttons/Button';
import DigitalAssetCollectionPerkBadge from '@cohort/merchants/components/digital-assets/DigitalAssetCollectionPerkBadge';
import {EmptyContentContainer} from '@cohort/merchants/components/EmptyContentContainer';
import DraggableList from '@cohort/merchants/components/form/DraggableList';
import {ConfirmationModal} from '@cohort/merchants/components/modals/ConfirmationModal';
import {PerkListItem} from '@cohort/merchants/components/perks/PerkListItem';
import {digitalAssetCollectionsKeys} from '@cohort/merchants/hooks/api/DigitalAssetCollections';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {useCurrentDigitalAssetCollection} from '@cohort/merchants/hooks/contexts/currentDigitalAssetCollection';
import {useDigitalAssetCollectionPageStore} from '@cohort/merchants/hooks/stores/digitalAssetCollectionPage';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import {usePrompt} from '@cohort/merchants/hooks/usePrompt';
import {patchDigitalAssetCollection} from '@cohort/merchants/lib/api/DigitalAssetCollections';
import {
  trackDigitalAssetCollectionPerkAdded,
  trackDigitalAssetCollectionPerkRemoved,
  trackDigitalAssetCollectionPerksAssociationsModalOpened,
  trackDigitalAssetCollectionPerksPageViewed,
  trackDigitalAssetCollectionPerksReordered,
  trackDigitalAssetCollectionPerksSaveButtonClicked,
} from '@cohort/merchants/lib/Tracking';
import AssociatePerkModal from '@cohort/merchants/pages/digital-asset-collections/digital-asset-collection/perks/AssociatePerkModal';
import {Gift} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import React, {useEffect, useId, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {equals} from 'remeda';

type PerksSelectionStatus = 'default' | 'added' | 'removed';

interface PerksSelection {
  perk: PerkADto;
  state: PerksSelectionStatus;
}

interface DigitalAssetCollectionPerksPageProps {
  setRefreshId: (id: string) => void;
}

export const DigitalAssetCollectionPerksPage: React.FC<DigitalAssetCollectionPerksPageProps> = ({
  setRefreshId,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const merchantId = useUserSessionStore(store => store.merchantId!);
  const queryClient = useQueryClient();
  const {t} = useTranslation('pages', {
    keyPrefix: 'digitalAssetCollections.digitalAssetCollection.perks.page',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const setFooter = useDigitalAssetCollectionPageStore(store => store.setFooter);
  const digitalAssetCollection = useCurrentDigitalAssetCollection();
  const nextRefreshId = useId();
  const [showAddModal, setShowAddModal] = useState(() => searchParams.has('add'));
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [listState, setListState] = useState<Array<PerksSelection>>(
    digitalAssetCollection.perks.map(perk => {
      return {
        perk: perk,
        state: 'default',
      };
    })
  );
  const initialPerkIds = digitalAssetCollection.perks.map(c => c.id);
  const currentPerkIds = listState.filter(c => c.state !== 'removed').map(c => c.perk.id);

  useEffect(() => {
    trackDigitalAssetCollectionPerksPageViewed(digitalAssetCollection);
  }, [digitalAssetCollection]);

  const {isLoading, mutate: updateDigitalAssetCollection} = useCohortMutation({
    mutationFn: async () =>
      patchDigitalAssetCollection(merchantId, digitalAssetCollection.id, {
        perkIds: listState.filter(c => c.state !== 'removed').map(c => c.perk.id),
      }),
    notifyErrorMessage: t('notificationErrorUpdate'),
    notifySuccessMessage: t('notificationSuccess'),
    onSuccess: async () => {
      await queryClient.invalidateQueries(digitalAssetCollectionsKeys.list(merchantId));
      await queryClient.invalidateQueries(
        digitalAssetCollectionsKeys.getById(merchantId, digitalAssetCollection.id)
      );
      const newSearchParams = {...searchParams};

      // Remove the add query param on save to prevent reopening the modal since we remount the entire component
      searchParams.delete('add');
      setSearchParams(newSearchParams);
      setRefreshId(nextRefreshId);
    },
  });

  useEffect(() => {
    if (!equals(initialPerkIds, currentPerkIds)) {
      setFooter(
        <div
          data-testid="submit-footer"
          className="-m-px flex justify-end gap-4 border-l border-t px-8 py-4"
        >
          <Button
            type="submit"
            data-testid="save"
            loading={isLoading}
            onClick={() => {
              setShowConfirmationModal(true);
            }}
          >
            {t('buttonSave')}
          </Button>
        </div>
      );
    } else {
      setFooter(undefined);
    }
    return () => setFooter(undefined);
  }, [setFooter, t, isLoading, initialPerkIds, currentPerkIds]);

  usePrompt(t('promptLostChanges'), !equals(initialPerkIds, currentPerkIds));

  const onAdd = (addedPerks: PerkADto[]): void => {
    let perksToAdd = addedPerks;
    const addedPerksIds = addedPerks.map(p => p.id);
    const changedRemovedItems = listState.map(item => {
      if (addedPerksIds.includes(item.perk.id)) {
        trackDigitalAssetCollectionPerkAdded(digitalAssetCollection, item.perk);
        perksToAdd = perksToAdd.filter(perk => perk.id !== item.perk.id);
        if (item.state === 'removed') {
          return {...item, state: 'default'} as PerksSelection;
        }
      }
      return item;
    });
    const newList: Array<PerksSelection> = [
      ...changedRemovedItems,
      ...perksToAdd.map(perk => {
        return {perk, state: 'added' as PerksSelectionStatus};
      }),
    ];
    setListState(newList);
  };

  const onRemove = (perk: PerkADto): void => {
    const newList: Array<PerksSelection> = listState
      .map(item => {
        if (item.perk.id === perk.id) {
          trackDigitalAssetCollectionPerkRemoved(digitalAssetCollection, item.perk);
          if (item.state === 'default') {
            return {...item, state: 'removed'};
          }
          if (item.state === 'added') {
            return undefined;
          }
        }
        return item;
      })
      .filter(c => c !== undefined) as Array<PerksSelection>;

    setListState(newList);
  };

  const onRemoveCancel = (perk: PerkADto): void => {
    const newList: Array<PerksSelection> = listState.map(c => {
      if (c.perk.id === perk.id) {
        return {...c, state: 'default'};
      }
      return c;
    });
    setListState(newList);
  };

  const onReorder = (itemIds: string[]): void => {
    trackDigitalAssetCollectionPerksReordered(digitalAssetCollection);
    const newList = [
      ...listState.sort((a, b) => itemIds.indexOf(a.perk.id) - itemIds.indexOf(b.perk.id)),
    ];
    setListState(newList);
  };

  const addDigitalAssetCollectionButton = (
    <Button
      key="add-perk-button"
      data-testid="add-perk-button"
      onClick={() => {
        trackDigitalAssetCollectionPerksAssociationsModalOpened(digitalAssetCollection);
        setShowAddModal(true);
      }}
    >
      {t('buttonAddPerks')}
    </Button>
  );

  const headerConfig = {
    title: t('title'),
    subtitle: t('subtitle'),
    topRightElements:
      digitalAssetCollection.perks.length === 0 && listState.length === 0
        ? []
        : [addDigitalAssetCollectionButton],
    titleBadge: (
      <DigitalAssetCollectionPerkBadge perksCount={digitalAssetCollection.perks.length} />
    ),
  };

  return (
    <AdminPage header={headerConfig}>
      {listState.length > 0 ? (
        <DraggableList
          handleOnReorder={onReorder}
          items={listState.map(item => {
            return {
              item: (
                <PerkListItem
                  perk={item.perk}
                  handleOnRemove={onRemove}
                  isAdded={item.state === 'added'}
                  isRemoved={item.state === 'removed'}
                  handleOnRemoveCancel={onRemoveCancel}
                />
              ),
              id: item.perk.id,
              isDragDisabled: item.state === 'removed',
            };
          })}
        />
      ) : (
        <EmptyContentContainer
          icon={<Gift size={40} className="text-slate-400" />}
          text={t('labelEmpty')}
          description={t('placeholderEmpty')}
          button={addDigitalAssetCollectionButton}
        />
      )}
      <AssociatePerkModal
        show={showAddModal}
        handleOnClose={() => setShowAddModal(false)}
        digitalAssetCollection={digitalAssetCollection}
        hiddenPerkIds={listState
          .filter(c => c.state === 'added' || c.state === 'default')
          .map(c => c.perk.id)}
        handleOnAdd={onAdd}
      />
      {showConfirmationModal && (
        <ConfirmationModal
          title={t('modalTitle')}
          content={t('modalText')}
          submitButtonText={t('modalButton')}
          show={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          onSubmit={() => {
            trackDigitalAssetCollectionPerksSaveButtonClicked(digitalAssetCollection);
            updateDigitalAssetCollection();
            setShowConfirmationModal(false);
          }}
        />
      )}
    </AdminPage>
  );
};

const DigitalAssetCollectionPerksPageWrapper = (): JSX.Element => {
  const [refreshId, setRefreshId] = useState<string>(useId());

  // We need to remount the entire component on save to reset the perks list state
  // Found it was the easiest way to do it
  return <DigitalAssetCollectionPerksPage key={refreshId} setRefreshId={setRefreshId} />;
};

export default DigitalAssetCollectionPerksPageWrapper;
